import { Directive, ElementRef, HostListener, inject, input, signal } from '@angular/core';

export interface AssistantMovementBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Session-local launcher placement. Pointer capture keeps dragging stable outside the button. */
@Directive({ selector: '[appMovableAssistant]', exportAs: 'movableAssistant' })
export class MovableAssistantDirective {
  readonly movementBounds = input<(() => AssistantMovementBounds | null) | null>(null);
  private readonly element = inject<ElementRef<HTMLButtonElement>>(ElementRef);
  readonly placement = signal('bottom-right');
  private position: { x: number; y: number } | null = null;
  private drag?: { id: number; x: number; y: number; left: number; top: number; moved: boolean };
  private suppressClick = false;
  private readonly inset = 24;
  private undockedPosition?: {
    position: { x: number; y: number } | null;
    placement: string;
    styles: { left: string; top: string; right: string; bottom: string };
  };

  /** Called when the reserved dock or its containing column changes size. */
  refreshBounds(): void {
    const button = this.element.nativeElement;
    const bounds = this.movementBounds()?.();
    if (!bounds) {
      if (this.undockedPosition) {
        Object.assign(button.style, this.undockedPosition.styles);
        this.position = this.undockedPosition.position;
        this.placement.set(this.undockedPosition.placement);
        this.undockedPosition = undefined;
        this.resize();
      }
      return;
    }
    this.undockedPosition ??= {
      position: this.position,
      placement: this.placement(),
      styles: { left: button.style.left, top: button.style.top,
        right: button.style.right, bottom: button.style.bottom },
    };
    const rect = button.getBoundingClientRect();
    this.setPosition(this.position?.x ?? rect.left, this.position?.y ?? rect.top);
  }

  @HostListener('pointerdown', ['$event'])
  start(event: PointerEvent): void {
    if (this.element.nativeElement.disabled) return;
    if (event.button !== 0 || !event.isPrimary) return;
    this.suppressClick = false;
    const rect = this.element.nativeElement.getBoundingClientRect();
    this.drag = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      moved: false,
    };
    this.element.nativeElement.setPointerCapture(event.pointerId);
  }

  @HostListener('pointermove', ['$event'])
  move(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) < 6) return;
    drag.moved = true;
    this.placement.set('custom');
    this.setPosition(drag.left + dx, drag.top + dy);
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  end(event: PointerEvent): void {
    if (!this.drag || event.pointerId !== this.drag.id) return;
    this.suppressClick = this.drag.moved;
    this.drag = undefined;
    const button = this.element.nativeElement;
    if (button.hasPointerCapture(event.pointerId)) button.releasePointerCapture(event.pointerId);
  }

  /** Called by the component before toggling so a completed drag never opens the chat. */
  consumeDragClick(): boolean {
    const suppressed = this.suppressClick;
    this.suppressClick = false;
    return suppressed;
  }

  @HostListener('keydown', ['$event'])
  keyboard(event: KeyboardEvent): void {
    if (this.element.nativeElement.disabled) return;
    this.suppressClick = false;
    if (event.key === 'Home') {
      event.preventDefault();
      this.place('bottom-right');
    } else if (
      event.altKey &&
      ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)
    ) {
      event.preventDefault();
      const rect = this.element.nativeElement.getBoundingClientRect();
      const x = rect.left + (event.key === 'ArrowLeft' ? -24 : event.key === 'ArrowRight' ? 24 : 0);
      const y = rect.top + (event.key === 'ArrowUp' ? -24 : event.key === 'ArrowDown' ? 24 : 0);
      this.placement.set('custom');
      this.setPosition(x, y);
    }
  }

  /** Menu alternative to dragging, also usable by touch and assistive technology. */
  place(placement: string): void {
    if (!['bottom-right', 'bottom-left', 'top-right', 'top-left'].includes(placement)) return;
    this.placement.set(placement);
    const rect = this.element.nativeElement.getBoundingClientRect();
    this.setPosition(
      placement.endsWith('right') ? window.innerWidth - rect.width - this.inset : this.inset,
      placement.startsWith('bottom') ? window.innerHeight - rect.height - this.inset : this.inset,
    );
  }

  @HostListener('window:resize')
  resize(): void {
    if (this.movementBounds()?.()) { this.refreshBounds(); return; }
    if (!this.position) return;
    if (this.placement() !== 'custom') this.place(this.placement());
    else this.setPosition(this.position.x, this.position.y);
  }

  private setPosition(x: number, y: number): void {
    const button = this.element.nativeElement;
    const rect = button.getBoundingClientRect();
    const bounds = this.movementBounds()?.();
    const minLeft = bounds?.left ?? 0;
    const minTop = bounds?.top ?? 0;
    const left = Math.max(minLeft, Math.min(x, Math.max(minLeft, (bounds?.right ?? window.innerWidth) - rect.width)));
    const top = Math.max(minTop, Math.min(y, Math.max(minTop, (bounds?.bottom ?? window.innerHeight) - rect.height)));
    this.position = { x: left, y: top };
    Object.assign(button.style, {
      left: `${left}px`,
      top: `${top}px`,
      right: 'auto',
      bottom: 'auto',
    });
  }
}
